import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Document } from 'react-pdf';
import PdfSeamlessPage from '@/components/common/asset/PdfSeamlessPage';
import { Room, RoomEvent, Track, type Participant, type TrackPublication } from 'livekit-client';

if (typeof globalThis.URL !== 'undefined' && typeof globalThis.URL.parse !== 'function') {
  const OriginalURL = globalThis.URL;
  (globalThis as any).URL = class URL extends OriginalURL {
    static parse(url: string, base?: string): URL | null {
      try {
        return new OriginalURL(url, base);
      } catch {
        return null;
      }
    }
  };
}
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchPublicWebinarSession } from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useParticipants,
  useRoomContext,
  VideoTrack,
} from '@livekit/components-react';
import {
  DEFAULT_CAM_LAYOUT,
  computeCamLayoutRects,
  fitCamLayout,
  pctRectStyle,
  scaleCamLayoutPx,
  type CamLayoutConfig,
  type CamLayoutRects,
} from '@/lib/camLayouts';
import { useRoomCamLayout } from '@/components/pages/live/useCamLayoutTransport';
import { fitBox, useAreaSize } from '@/lib/useFittedBox';
import { CameraOff, Hourglass } from 'lucide-react';
import { toSlidesProxyUrl } from '@/lib/slidesProxyUrl';
import i18n from '@/i18n';
import { toWebinarLng } from '@/lib/webinarLanguage';
import { getAppLanguage } from '@/i18n/languages';

const RECORDING_BLACK_PLACEHOLDER_TRACK_NAME = 'recording_black_placeholder';

const unpublishPresentationAudio = async (room: Room): Promise<void> => {
  const pub = Array.from(room.localParticipant.trackPublications.values()).find(
    (p) => p.trackName === 'presentation_audio' || p.source === Track.Source.ScreenShareAudio,
  );
  if (pub?.track) await room.localParticipant.unpublishTrack(pub.track, true);
};

function createBlackCanvasVideoTrack(): { track: MediaStreamTrack; cleanup: () => void } | null {
  const canvas = document.createElement('canvas');
  canvas.width = 1280;
  canvas.height = 720;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let rafId = 0;
  const drawLoop = () => {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    rafId = globalThis.requestAnimationFrame(drawLoop);
  };
  rafId = globalThis.requestAnimationFrame(drawLoop);

  const stream = canvas.captureStream(30);
  const track = stream.getVideoTracks()[0];
  if (!track) {
    globalThis.cancelAnimationFrame(rafId);
    return null;
  }

  return {
    track,
    cleanup: () => {
      globalThis.cancelAnimationFrame(rafId);
      track.stop();
    },
  };
}

async function unpublishRecordingBlackPlaceholder(room: Room): Promise<void> {
  const pub = Array.from(room.localParticipant.trackPublications.values()).find(
    (p) => p.trackName === RECORDING_BLACK_PLACEHOLDER_TRACK_NAME,
  );
  if (pub?.track) await room.localParticipant.unpublishTrack(pub.track, true);
}

/** Caméra réellement exploitable (évite VideoTrack noir quand la pub existe encore mais piste muée / ended). */
function getLivePresenterCameraTrackRef(
  participant: Participant,
):
  | { participant: Participant; source: typeof Track.Source.Camera; publication: TrackPublication }
  | undefined {
  const pub = participant.getTrackPublication(Track.Source.Camera);
  const track = pub?.track;
  if (!pub || !track) return undefined;
  if (pub.isMuted || track.isMuted) return undefined;
  const mst = track.mediaStreamTrack;
  if (mst.readyState === 'ended' || !mst.enabled) return undefined;
  return { participant, source: Track.Source.Camera as const, publication: pub };
}

/**
 * Vrai présentateur humain : exclut soi-même et les participants techniques (autres egress, agents).
 * On s'aligne sur LiveKitRoomContent : les vrais publishers ont une identité `presenter-`/`speaker-`,
 * ce que n'ont pas les autres recorders egress (kind non fiable côté egress, on filtre donc par identité).
 */
function isRemotePresenter(participant: Participant): boolean {
  if (participant.isLocal || participant.permissions?.canPublish !== true) return false;
  const identity = participant.identity || '';
  return identity.startsWith('presenter-') || identity.startsWith('speaker-');
}

function hasRemotePresenterCameraTrack(participants: Participant[]): boolean {
  for (const p of participants) {
    if (!isRemotePresenter(p)) continue;
    if (getLivePresenterCameraTrackRef(p)) return true;
  }
  return false;
}

const publishPresentationAudioOnly = async (
  videoElement: HTMLVideoElement,
  room: Room,
): Promise<void> => {
  await unpublishPresentationAudio(room);
  const stream =
    (videoElement as any).captureStream?.(30) || (videoElement as any).mozCaptureStream?.(30);
  if (!stream) return;
  const audioTrack = stream.getAudioTracks()[0];
  if (!audioTrack) return;
  await room.localParticipant.publishTrack(audioTrack, {
    name: 'presentation_audio',
    source: Track.Source.ScreenShareAudio,
  });
};

// Layout unique appliqué à tous les flux egress (p1080/p720/p480/p360, tous en 16:9).
// On garde le rendu desktop quelle que soit la résolution : c'est le back qui encode chaque flux.
const RECORDING_LAYOUT = {
  gridNameText: 'text-[20px]',
  captureArea: 'h-stretch',
  lkRoomClassName: 'h-screen',
} as const;

type PresenterCamItem = {
  participant: Participant;
  camTrack:
    | { participant: Participant; source: Track.Source.Camera; publication: TrackPublication }
    | undefined;
};

/**
 * Vignettes caméra posées sur le cadre aux rectangles calculés par `computeCamLayoutRects`
 * (colonne, rangée ou coin selon l'option). Les présentateurs au-delà des rectangles disponibles
 * ne sont pas affichés (capacité de la disposition).
 */
function MainStageCamTiles({
  presenterItems,
  rects,
  frameHeightPx,
  onRemotePresenterVideoReady,
  tileBackgroundColor,
}: {
  presenterItems: PresenterCamItem[];
  rects: CamLayoutRects;
  frameHeightPx: number | null | undefined;
  onRemotePresenterVideoReady: () => void;
  tileBackgroundColor: string;
}) {
  const radius = scaleCamLayoutPx(rects.camRadiusPx, frameHeightPx);
  return (
    <>
      {rects.cams.map((rect, index) => {
        const item = presenterItems[index];
        if (!item?.camTrack) return null;
        return (
          <div
            key={`${item.participant.identity}-${index}`}
            // `[&_video]:scale-[1.005]` : léger overscan clippé, sinon un liseré de fond d'un pixel
            // affleure sur le bord droit et casse l'angle arrondi.
            className="absolute z-10 overflow-hidden [&_video]:scale-[1.005]"
            style={{
              ...pctRectStyle(rect),
              borderRadius: radius,
              backgroundColor: tileBackgroundColor,
            }}
          >
            <VideoTrack
              trackRef={item.camTrack}
              className="w-full h-full object-cover"
              onPlaying={onRemotePresenterVideoReady}
            />
          </div>
        );
      })}
    </>
  );
}

const RecordingStage = ({
  layout,
  setLayout,
  onRemotePresenterVideoReady,
  onPresentationContentReady,
  onMainStageChange,
  backgroundColor,
  secondaryBackgroundColor,
  theme = 'LIGHT',
}: {
  layout: {
    type: string;
    active: {
      type: string;
      url?: string;
      page?: number;
      paused?: boolean;
      muted?: boolean;
      currentTime?: number;
    } | null;
  };
  setLayout: (layout: {
    type: string;
    active: {
      type: string;
      url?: string;
      page?: number;
      paused?: boolean;
      muted?: boolean;
      currentTime?: number;
    } | null;
  }) => void;
  onRemotePresenterVideoReady: () => void;
  onPresentationContentReady: () => void;
  /** Indique si la scène occupe le « main stage » (PDF/VIDÉO via metadata, ou partage d’écran détecté par pistes). */
  onMainStageChange?: (active: boolean) => void;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  theme?: 'LIGHT' | 'DARK';
}) => {
  const placeholderBackground =
    secondaryBackgroundColor || (theme === 'DARK' ? '#1F1F1F' : '#F3F3F3');
  const placeholderColor = theme === 'DARK' ? '#FFFFFF' : '#000000';
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfBox, setPdfBox] = useState({ w: 0, h: 0 });
  const [pdfPageAspect, setPdfPageAspect] = useState<number | null>(null);
  // Boîte vidéo ajustée au ratio réel du fichier : object-contain la remplit (scale up compris, ce que
  // `max-w/max-h-full` ne fait pas → une vidéo basse résolution restait minuscule).
  const [videoAspect, setVideoAspect] = useState<number | null>(null);
  const { ref: videoAreaRef, size: videoArea } = useAreaSize<HTMLDivElement>();
  const videoFittedBox = videoAspect != null ? fitBox(videoArea.w, videoArea.h, videoAspect) : null;
  // Hauteur réelle du cadre baké : chaque egress rend sa propre résolution (1080/720/480/360).
  // Les rayons en px de la mise en page y sont mis à l’échelle, sinon ils pèsent 3× plus lourd en 360.
  const { ref: stageAreaRef, size: stageArea } = useAreaSize<HTMLDivElement>();
  const frameHeightPx = stageArea.h;

  const room = useRoomContext();
  const participants = useParticipants();

  useEffect(() => {
    const sync = (metadata?: string) => {
      if (!metadata) return;
      try {
        const data = JSON.parse(metadata);
        setLayout({ type: data.layout || 'grid', active: data.activeAsset || null });
      } catch (e) {
        console.error('Metadata error', e);
      }
    };
    const refreshFromRoom = () => sync(room.metadata);
    refreshFromRoom();
    room.on(RoomEvent.RoomMetadataChanged, sync);
    room.on(RoomEvent.Connected, refreshFromRoom);
    room.on(RoomEvent.Reconnected, refreshFromRoom);
    return () => {
      room.off(RoomEvent.RoomMetadataChanged, sync);
      room.off(RoomEvent.Connected, refreshFromRoom);
      room.off(RoomEvent.Reconnected, refreshFromRoom);
    };
  }, [room]);

  // Mise en page caméras portée par la metadata de room (défaut du workspace à la création, puis
  // écritures des régies) ; en attendant, disposition par défaut.
  const [camLayoutPreference, setCamLayoutPreference] =
    useState<CamLayoutConfig>(DEFAULT_CAM_LAYOUT);
  useRoomCamLayout(room, setCamLayoutPreference);

  const presenters = useMemo(() => participants.filter(isRemotePresenter), [participants]);

  const presenterItems = useMemo((): PresenterCamItem[] => {
    return presenters.map((participant) => {
      const camTrack = getLivePresenterCameraTrackRef(participant);
      return { participant, camTrack };
    });
  }, [presenters]);

  // Grille (sans contenu principal) : caméras actives seules, sinon vignettes « caméra désactivée »,
  // sinon (aucun présentateur) le visuel « le présentateur arrive bientôt » rendu plus bas.
  const presentersWithCamera = useMemo(
    () => presenterItems.filter((p) => p.camTrack),
    [presenterItems],
  );
  const gridPresenterItems = useMemo(
    () => (presentersWithCamera.length > 0 ? presentersWithCamera : presenterItems),
    [presentersWithCamera, presenterItems],
  );

  const pdfContainerAspect = pdfBox.h > 0 ? pdfBox.w / pdfBox.h : 16 / 9;
  const pdfPageDims =
    pdfPageAspect == null
      ? { width: pdfBox.w || undefined }
      : pdfPageAspect >= pdfContainerAspect
        ? { width: Math.floor(pdfBox.w) }
        : { height: Math.floor(pdfBox.h) };

  // Boîte figée du calque de page : il doit garder sa taille quand react-pdf retire son canvas,
  // sinon l'instantané de la page précédente s'écrase avec lui et le flash réapparaît.
  const pdfPageBox =
    pdfPageAspect == null || pdfBox.w <= 0 || pdfBox.h <= 0
      ? undefined
      : pdfPageAspect >= pdfContainerAspect
        ? { width: Math.floor(pdfBox.w), height: Math.floor(pdfBox.w / pdfPageAspect) }
        : { width: Math.floor(pdfBox.h * pdfPageAspect), height: Math.floor(pdfBox.h) };

  /** Première piste partage d’écran distante (présentateur / admin). */
  const primaryScreenShareTrack = useMemo(() => {
    for (const participant of presenters) {
      const pub = participant.getTrackPublication(Track.Source.ScreenShare);
      if (pub?.track) {
        return { participant, publication: pub, source: Track.Source.ScreenShare as const };
      }
    }
    return undefined;
  }, [presenters]);

  /** PDF / vidéo projetés : uniquement via metadata + URL. */
  const isVideoPresentation =
    layout.type === 'presentation' &&
    layout.active?.type === 'VIDEO' &&
    Boolean(layout.active?.url);
  const isPdfPresentation =
    layout.type === 'presentation' && layout.active?.type === 'PDF' && Boolean(layout.active?.url);

  /** Partage d’écran : pas de layout « présentation », on s’appuie sur la piste ScreenShare. */
  const showScreenShareTrackLayout =
    Boolean(primaryScreenShareTrack) && !isVideoPresentation && !isPdfPresentation;

  const occupiesMainStage = isVideoPresentation || isPdfPresentation || showScreenShareTrackLayout;

  useEffect(() => {
    onMainStageChange?.(occupiesMainStage);
  }, [occupiesMainStage, onMainStageChange]);

  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el || !isPdfPresentation) return;
    const measure = () => setPdfBox({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPdfPresentation]);

  const hasVisibleGridSlots = gridPresenterItems.length > 0;
  // Le live a-t-il déjà eu du contenu/présentateur ? Sert à distinguer « pas encore démarré »
  // d'une « coupure en cours de live » quand la scène se vide.
  const hasBeenLiveRef = useRef(false);
  useEffect(() => {
    if (occupiesMainStage || hasVisibleGridSlots) hasBeenLiveRef.current = true;
  }, [occupiesMainStage, hasVisibleGridSlots]);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (
      layout.type !== 'presentation' ||
      layout.active?.type !== 'VIDEO' ||
      !layout.active?.url ||
      !videoRef.current
    )
      return;
    const video = videoRef.current;
    const handleCanPlay = () => {
      publishPresentationAudioOnly(video, room).catch(console.error);
    };
    video.addEventListener('loadeddata', handleCanPlay);
    if (video.readyState >= 2) handleCanPlay();
    return () => {
      video.removeEventListener('loadeddata', handleCanPlay);
      unpublishPresentationAudio(room).catch(console.error);
    };
  }, [layout.type, layout.active?.type, layout.active?.url, room]);

  useEffect(() => {
    if (layout.type !== 'presentation' || layout.active?.type !== 'VIDEO' || !videoRef.current)
      return;
    const video = videoRef.current;
    const { paused, muted, currentTime } = layout.active;

    if (typeof paused === 'boolean') {
      if (paused) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    }
    if (typeof muted === 'boolean') {
      video.muted = muted;
    }
    if (typeof currentTime === 'number' && !Number.isNaN(currentTime)) {
      const delta = Math.abs(video.currentTime - currentTime);
      if (delta > 0.5) {
        video.currentTime = currentTime;
      }
    }
  }, [
    layout.type,
    layout.active?.type,
    layout.active?.paused,
    layout.active?.muted,
    layout.active?.currentTime,
  ]);

  // Coupure : le(s) présentateur(s) ont quitté la room après que le live a démarré.
  const isPresenterGone = hasBeenLiveRef.current && presenters.length === 0;
  // Dimensionnée pour le canvas 1920px : le player du spectateur la réduit (~×0,7), elle doit
  // donc être ~1/3 plus grande que son équivalente DOM (overlay de VideoHlsView) pour paraître
  // à la même taille à l'écran.
  const interruptionCard = (
    <div className="flex max-w-2xl items-start gap-4 rounded-2xl bg-white p-8 shadow-[0px_4px_6px_0px_rgba(28,43,53,0.08)]">
      <Hourglass className="size-10 shrink-0 text-[#2C6CFF]" aria-hidden />
      <div className="flex flex-col gap-2">
        <p className="text-3xl font-medium leading-10 text-[#2C6CFF]">
          {i18n.t('liveSettings.liveStreamInterruptedTitle')}
        </p>
        <p className="text-xl leading-8 text-[#0C0E21]">
          {i18n.t('liveSettings.liveStreamInterruptedBody')}
        </p>
      </div>
    </div>
  );

  // Géométrie de la scène : avec contenu, les caméras sont posées selon l'option choisie par la régie
  // et le contenu occupe la boîte restante (tout le cadre, bord à bord, sans caméra) ; sans contenu,
  // grille de caméras. Les présentateurs sans caméra n'occupent une case que dans la grille.
  const stageRects = computeCamLayoutRects(
    fitCamLayout(camLayoutPreference, presentersWithCamera.length),
    occupiesMainStage ? presentersWithCamera.length : gridPresenterItems.length,
    occupiesMainStage,
  );
  const contentRadiusPx = scaleCamLayoutPx(stageRects.contentRadiusPx, frameHeightPx);
  const mainStageContentBox = 'absolute flex items-center justify-center';
  const mainStageContentBoxStyle = stageRects.content
    ? pctRectStyle(stageRects.content)
    : undefined;
  const mainStageContentRounded = 'rounded-[var(--live-radius,0px)]';
  const hasPresenterCams = presentersWithCamera.length > 0;
  const camTilesProps = {
    presenterItems: presentersWithCamera,
    rects: stageRects,
    frameHeightPx,
    onRemotePresenterVideoReady,
    tileBackgroundColor: backgroundColor || '#FFFFFF',
  };

  return (
    <div
      ref={stageAreaRef}
      className="relative w-full h-full overflow-hidden"
      style={
        {
          backgroundColor: backgroundColor || '#FFFFFF',
          // Rayon du contenu posé en variable : le calque PDF (canvas snapshot) le consomme aussi.
          '--live-radius': `${contentRadiusPx}px`,
        } as CSSProperties
      }
    >
      {layout.type === 'presentation' && layout.active && layout.active?.type === 'VIDEO' && (
        <div className="w-full h-full relative">
          <div ref={videoAreaRef} className={mainStageContentBox} style={mainStageContentBoxStyle}>
            <div
              className={`relative overflow-hidden ${mainStageContentRounded} ${
                videoFittedBox ? '' : 'h-full w-full'
              }`}
              style={
                videoFittedBox
                  ? { width: videoFittedBox.width, height: videoFittedBox.height }
                  : undefined
              }
            >
              <video
                ref={videoRef}
                src={layout.active.url}
                muted={layout.active.muted ?? false}
                className="w-full h-full object-contain"
                onLoadedData={() => onPresentationContentReady()}
                onLoadedMetadata={(e) => {
                  const w = e.currentTarget.videoWidth;
                  const h = e.currentTarget.videoHeight;
                  if (h > 0) {
                    const aspect = w / h;
                    setVideoAspect((previous) =>
                      previous != null && Math.abs(previous - aspect) < 0.001 ? previous : aspect,
                    );
                  }
                }}
              >
                <track
                  kind="captions"
                  srcLang={getAppLanguage(i18n.language).code}
                  label={i18n.t('liveSettings.liveStreamNoCaptionsTrack')}
                />
              </video>
            </div>
          </div>
          <MainStageCamTiles {...camTilesProps} />
        </div>
      )}

      {layout.type === 'presentation' && layout.active && layout.active?.type === 'PDF' && (
        <div className="w-full h-full relative">
          <div
            ref={pdfContainerRef}
            className={`${mainStageContentBox} ${mainStageContentRounded} min-w-0 overflow-hidden`}
            style={mainStageContentBoxStyle}
          >
            {layout.active?.url ? (
              <Document
                file={toSlidesProxyUrl(layout.active.url)}
                onLoadSuccess={() => onPresentationContentReady()}
                onLoadError={(e) => console.error(e)}
                className={'flex items-center justify-center'}
                loading={<Skeleton className="w-full h-full rounded-[var(--live-radius,0px)]" />}
                error={
                  <div className="p-4 text-red-600 text-sm">
                    {i18n.t('webinars:live.recording.pdfLoadError')}
                  </div>
                }
                noData={
                  <div className="p-4 text-gray-500 text-sm">
                    {i18n.t('webinars:live.recording.noPdf')}
                  </div>
                }
              >
                <PdfSeamlessPage
                  wrapperClassName={`relative ${hasPresenterCams ? '[&>canvas[data-pdf-snapshot]]:rounded-[var(--live-radius,0px)]' : ''}`}
                  wrapperStyle={pdfPageBox}
                  pageNumber={layout.active?.page ?? 1}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  {...pdfPageDims}
                  onLoadSuccess={(page) => {
                    const { width, height } = page.getViewport({ scale: 1 });
                    if (!height) return;
                    const aspect = width / height;
                    setPdfPageAspect((previous) =>
                      previous != null && Math.abs(previous - aspect) < 0.001 ? previous : aspect,
                    );
                  }}
                  className={`bg-transparent! overflow-hidden ${mainStageContentRounded}`}
                />
              </Document>
            ) : (
              <div className="text-gray-400 text-xl font-medium">
                {layout.active?.type === 'PDF'
                  ? i18n.t('webinars:live.recording.waitingPresentation')
                  : i18n.t('webinars:live.recording.waitingVideo')}
              </div>
            )}
          </div>
          <MainStageCamTiles {...camTilesProps} />
        </div>
      )}

      {showScreenShareTrackLayout && (
        <div className="w-full h-full relative">
          <div
            className={`${mainStageContentBox} ${mainStageContentRounded} overflow-hidden`}
            style={mainStageContentBoxStyle}
          >
            {primaryScreenShareTrack ? (
              // Overscan léger : le flux partage d'écran a un liseré noir egress sur son propre bord
              // (interne au cadre, donc non masqué par l'overscan du player). On le pousse hors du clip.
              <VideoTrack
                trackRef={primaryScreenShareTrack}
                className="w-full h-full object-contain origin-top-left scale-[1.005]"
                style={{ backgroundColor }}
                onPlaying={onPresentationContentReady}
              />
            ) : (
              <div className="text-gray-400 text-xl font-medium">
                {i18n.t('webinars:live.recording.waitingScreenShare')}
              </div>
            )}
          </div>
          <MainStageCamTiles {...camTilesProps} />
        </div>
      )}

      {!occupiesMainStage && (
        <div className="w-full h-full flex items-center justify-center">
          {!hasVisibleGridSlots ? (
            // Scène vide : fond de base (beige + filigrane Welya, cover), + card coupure si le live
            // a déjà démarré (sinon simple attente avant le début).
            <div
              className="relative w-full h-full overflow-hidden"
              style={{ backgroundColor: theme === 'DARK' ? '#1F1F1F' : '#EEEAE5' }}
            >
              <img
                src={`${import.meta.env.BASE_URL}assets/welya-watermark.svg`}
                alt=""
                aria-hidden
                className={`absolute inset-x-0 bottom-0 top-[12%] w-full scale-90 object-cover ${
                  theme === 'DARK' ? 'invert' : ''
                }`}
              />
              {hasBeenLiveRef.current && (
                <div className="absolute inset-0 flex items-center justify-center px-6">
                  {interruptionCard}
                </div>
              )}
            </div>
          ) : (
            <div className="relative w-full h-full">
              {gridPresenterItems
                .slice(0, stageRects.cams.length)
                .map(({ participant, camTrack }, index) => (
                  <div
                    key={`${participant.identity}-${index}`}
                    className="absolute overflow-hidden"
                    style={{
                      ...pctRectStyle(stageRects.cams[index]),
                      borderRadius: scaleCamLayoutPx(stageRects.camRadiusPx, frameHeightPx),
                      backgroundColor: camTrack
                        ? backgroundColor || '#FFFFFF'
                        : placeholderBackground,
                    }}
                  >
                    {camTrack ? (
                      <VideoTrack
                        trackRef={camTrack}
                        className="w-full h-full object-cover"
                        onPlaying={onRemotePresenterVideoReady}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center gap-2"
                        style={{ color: placeholderColor }}
                      >
                        <CameraOff className="size-12" aria-hidden />
                        <span className="text-base">{i18n.t('liveSettings.cameraDisabled')}</span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Coupure avec contenu principal : la card passe par-dessus le contenu (en haut). */}
      {occupiesMainStage && isPresenterGone && (
        <div className="absolute inset-x-0 top-0 flex justify-center px-6 pt-6">
          {interruptionCard}
        </div>
      )}
    </div>
  );
};

function RecordingViewWithPlaceholder({
  layout,
  setLayout,
  backgroundColor,
  secondaryBackgroundColor,
  theme,
  onMediaReadySignal,
  onMainStageChange,
}: {
  layout: {
    type: string;
    active: {
      type: string;
      url?: string;
      page?: number;
      paused?: boolean;
      muted?: boolean;
      currentTime?: number;
    } | null;
  };
  setLayout: (layout: {
    type: string;
    active: {
      type: string;
      url?: string;
      page?: number;
      paused?: boolean;
      muted?: boolean;
      currentTime?: number;
    } | null;
  }) => void;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  theme?: 'LIGHT' | 'DARK';
  onMediaReadySignal: () => void;
  onMainStageChange?: (active: boolean) => void;
}) {
  const room = useRoomContext();
  const participants = useParticipants();
  const [placeholderReleased, setPlaceholderReleased] = useState(false);
  const [roomConnected, setRoomConnected] = useState(room.state === 'connected');

  const hasRemoteCamera = useMemo(
    () => hasRemotePresenterCameraTrack(participants),
    [participants],
  );

  const hasRemoteScreenShare = useMemo(() => {
    for (const p of participants) {
      if (!isRemotePresenter(p)) continue;
      if (p.getTrackPublication(Track.Source.ScreenShare)?.track) return true;
    }
    return false;
  }, [participants]);

  const hasUrlPresentation = useMemo(
    () =>
      layout.type === 'presentation' &&
      layout.active != null &&
      (layout.active.type === 'PDF' || layout.active.type === 'VIDEO') &&
      Boolean(layout.active.url),
    [layout.type, layout.active],
  );

  const urlBasedPresentation = useMemo(() => {
    if (layout.type !== 'presentation' || layout.active == null || !layout.active.url) {
      return false;
    }
    if (layout.active.type === 'PDF') {
      return true;
    }
    if (layout.active.type === 'VIDEO') {
      return layout.active.paused === false;
    }
    return false;
  }, [layout.type, layout.active]);

  /** Contenu principal : PDF/vidéo URL (metadata) ou partage d’écran détecté par pistes. */
  const isPresWithMainAsset = urlBasedPresentation || hasRemoteScreenShare;

  useEffect(() => {
    if (hasRemoteCamera) {
      setPlaceholderReleased(true);
    } else if (isPresWithMainAsset) {
      setPlaceholderReleased(true);
    } else {
      setPlaceholderReleased(false);
    }
  }, [hasRemoteCamera, isPresWithMainAsset]);

  /** Après une coupure WebRTC, relance le signal côté puppeteer/CDP (intervalle arrêté dans onDisconnected). */
  useEffect(() => {
    const updateRoomConnected = () => {
      setRoomConnected(room.state === 'connected');
    };
    updateRoomConnected();
    room.on(RoomEvent.Connected, updateRoomConnected);
    room.on(RoomEvent.Reconnected, updateRoomConnected);
    room.on(RoomEvent.Disconnected, updateRoomConnected);
    return () => {
      room.off(RoomEvent.Connected, updateRoomConnected);
      room.off(RoomEvent.Reconnected, updateRoomConnected);
      room.off(RoomEvent.Disconnected, updateRoomConnected);
    };
  }, [room]);

  useEffect(() => {
    const onReconnected = () => {
      if (room.state === 'connected') {
        onMediaReadySignal();
      }
    };
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room, onMediaReadySignal]);

  useEffect(() => {
    if (!roomConnected || hasRemoteCamera || hasRemoteScreenShare || hasUrlPresentation) {
      return;
    }
    const rafId = globalThis.requestAnimationFrame(() => {
      onMediaReadySignal();
    });
    return () => {
      globalThis.cancelAnimationFrame(rafId);
    };
  }, [
    roomConnected,
    hasRemoteCamera,
    hasRemoteScreenShare,
    hasUrlPresentation,
    onMediaReadySignal,
  ]);

  useEffect(() => {
    if (!roomConnected || placeholderReleased) {
      void unpublishRecordingBlackPlaceholder(room);
      return;
    }

    const created = createBlackCanvasVideoTrack();
    if (!created) return;

    void room.localParticipant
      .publishTrack(created.track, {
        name: RECORDING_BLACK_PLACEHOLDER_TRACK_NAME,
        source: Track.Source.Camera,
        videoEncoding: { maxBitrate: 80_000, maxFramerate: 30 },
      })
      .then(() => {
        onMediaReadySignal();
      })
      .catch((e) => {
        console.error('Recording black placeholder publish failed', e);
      });

    return () => {
      void unpublishRecordingBlackPlaceholder(room);
      created.cleanup();
    };
  }, [room, roomConnected, placeholderReleased, onMediaReadySignal]);

  const onRemotePresenterVideoReady = useCallback(() => {
    onMediaReadySignal();
    setPlaceholderReleased(true);
  }, [onMediaReadySignal]);

  const onPresentationContentReady = useCallback(() => {
    onMediaReadySignal();
  }, [onMediaReadySignal]);

  return (
    <RecordingStage
      layout={layout}
      setLayout={setLayout}
      onRemotePresenterVideoReady={onRemotePresenterVideoReady}
      onPresentationContentReady={onPresentationContentReady}
      onMainStageChange={onMainStageChange}
      backgroundColor={backgroundColor}
      secondaryBackgroundColor={secondaryBackgroundColor}
      theme={theme}
    />
  );
}

export default function LiveKitRecordingViewPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const serverUrl = params.get('url') || import.meta.env.VITE_LIVEKIT_WS_URL;
  const webinarId = params.get('webinarId');
  const sessionIdentifier = params.get('sessionIdentifier');
  const [layout, setLayout] = useState<{
    type: string;
    active: {
      type: string;
      url?: string;
      page?: number;
      paused?: boolean;
      muted?: boolean;
      currentTime?: number;
    } | null;
  }>({
    type: 'grid',
    active: null,
  });
  const dispatch = useAppDispatch();

  useEffect(() => {
    const html = document.documentElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;
    const previousHtmlHeight = html.style.height;
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    html.style.height = 'auto';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
      html.style.height = previousHtmlHeight;
    };
  }, []);

  const currentPublicWebinarSession = useAppSelector(
    (state: RootState) => state.webinar.currentPublicSession,
  );

  const backgroundColor = currentPublicWebinarSession?.visualIdentity?.backgroundColor || '#000000';
  const secondaryBackgroundColor =
    currentPublicWebinarSession?.visualIdentity?.secondaryBackgroundColor || undefined;
  const theme = (currentPublicWebinarSession?.visualIdentity?.theme || 'LIGHT') as 'LIGHT' | 'DARK';

  // Compositor headless : on cale i18n sur la langue du live (sinon libellés en langue ambiante).
  // Le tick force un re-render même salle vide (i18n.t direct ne re-render pas seul).
  const [, setLangTick] = useState(0);
  useEffect(() => {
    const language = currentPublicWebinarSession?.language;
    if (!language) return;
    const target = toWebinarLng(language);
    if (i18n.language === target) return;
    void i18n.changeLanguage(target).then(() => setLangTick((tick) => tick + 1));
  }, [currentPublicWebinarSession?.language]);

  useEffect(() => {
    if (!webinarId || !sessionIdentifier) return;
    dispatch(
      fetchPublicWebinarSession({
        webinarId: Number(webinarId),
        sessionIdentifier,
      }),
    );
  }, [webinarId, sessionIdentifier, dispatch]);
  const [mainStageActive, setMainStageActive] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Une seule « ticker » ; redémarrable après déconnexion (intervalle effacé). */
  const beginRecordingSignal = useCallback(() => {
    if (intervalRef.current != null) return;
    const tick = () => {
      (globalThis as any).livekitReady = true;
      document.body.dataset.livekitReady = 'true';
      console.log('START_RECORDING');
    };
    tick();
    intervalRef.current = globalThis.setInterval(tick, 500);
  }, []);

  useEffect(() => {
    (globalThis as any).livekitReady = false;
    delete document.body.dataset.livekitReady;
    return () => {
      (globalThis as any).livekitReady = false;
      delete document.body.dataset.livekitReady;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  if (!token)
    return (
      <div className="bg-black text-white h-screen flex items-center justify-center">
        {i18n.t('webinars:live.recording.invalidConfig')}
      </div>
    );

  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect={true}
      audio={false}
      video={false}
      style={{ scrollbarWidth: 'none' }}
      className={`${mainStageActive ? 'h-screen' : RECORDING_LAYOUT.lkRoomClassName} overflow-hidden`}
      onDisconnected={() => {
        console.log('END_RECORDING');
        (globalThis as any).livekitReady = false;
        delete document.body.dataset.livekitReady;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }}
      options={{ adaptiveStream: false, disconnectOnPageLeave: false }}
    >
      <div
        id="capture-area"
        className={`h-full ${mainStageActive ? 'h-stretch' : RECORDING_LAYOUT.captureArea} overflow-hidden`}
        style={{ backgroundColor }}
        // style={{
        //   width: 1920,
        //   height: 1080,
        //   backgroundColor,
        //   overflow: 'hidden',
        // }}
      >
        <RoomAudioRenderer />
        <RecordingViewWithPlaceholder
          layout={layout}
          setLayout={setLayout}
          onMediaReadySignal={beginRecordingSignal}
          onMainStageChange={setMainStageActive}
          backgroundColor={backgroundColor}
          secondaryBackgroundColor={secondaryBackgroundColor}
          theme={theme}
        />
      </div>
    </LiveKitRoom>
  );
}
